/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useMemo, useState } from "react";

import color from "color";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Typography from "@mui/material/Typography";

import useQueryBoard, { useLogoUrl } from "../../lib/hooks/useQueryBoard";
import { useLanguage, useLanguages } from "../../bit/language";
import { anim, modalUp } from "../Animations/utils";

import LocaleIcon from "../LocaleIcon";
import CloseIcon from "../../assets/icons/Close.svg";
import LogoImage from "../LogoImage";

import AISparkle from "../../assets/icons/AISparkle.svg";

const MUIFooterLanguageButton = styled(Button)(({ theme }) => ({
  width: "42rem",
  borderRadius: "2rem",
  borderWidth: "0.2rem",

  "&:active": {
    borderWidth: "0.2rem",
  },
  "&:hover": {
    borderWidth: "0.2rem",
  },
}));

const MUIModalLanguageButton = styled(Button)<{ isActive: boolean }>(
  ({ theme, isActive }) => ({
    marginBottom: "1.5rem",
    borderRadius: "2rem",
    borderColor: `${theme.palette.primary.main} !important`,
    backgroundColor: isActive
      ? theme.palette.primary.dark
      : theme.palette.primary.main,
  }),
);

const MUILanguageModal = styled("div")(({ theme }) => ({
  ".language-modal__content": {
    backgroundColor: color(theme.palette.background.default)
      .darken(0.025)
      .hex(),
  },
}));

const ConversationButton = styled(Button)(() => ({
  position: "absolute",
  right: 0,
  bottom: 0,
  width: "12rem",
  borderRadius: "2rem 0rem 0rem 0rem",
  borderWidth: "0.2rem",

  "&:active": {
    borderWidth: "0.2rem",
  },
  "&:hover": {
    borderWidth: "0.2rem",
  },
}));

const Footer = React.memo(() => {
  const { t } = useTranslation(["language"]);

  const navigate = useNavigate();
  const languages = useLanguages();

  const [locale, setLocale] = useLanguage();

  const [languageModal, setLanguageModal] = useState<boolean>(false);

  const { data, dataUpdatedAt } = useQueryBoard();

  const logoUrl = useLogoUrl();

  const isAIConversationEnabled = useMemo(
    () => data?.digitalReception?.metadata?.aiConversation || false,
    [],
  );

  const isConversationPage = useMemo(
    () => window.location.pathname === "/conversation",
    [window.location.pathname],
  );

  const renderModalLanguage = useCallback(
    (key: Language) => (
      <MUIModalLanguageButton
        key={key}
        data-value={key}
        isActive={locale === key}
        variant="contained"
        onClick={() => {
          setLocale(key);
          setLanguageModal(false);
        }}
        className="border"
      >
        <LocaleIcon
          name={key}
          width="3.2rem"
          height="3.2rem"
          className="position-absolute start-0 ms-5"
        />
        {t(`language:${key}`)}
      </MUIModalLanguageButton>
    ),
    [t, locale],
  );

  const renderLanguage = useCallback(
    (key: Language) => (
      <MUIFooterLanguageButton
        key={key}
        data-value={key}
        variant={locale === key ? "contained" : "outlined"}
        onClick={() => {
          setLocale(key);
        }}
      >
        {t(`language:${key}`)}
      </MUIFooterLanguageButton>
    ),
    [t, locale, setLocale],
  );

  const onClose = useCallback(() => {
    setLanguageModal(false);
  }, []);

  return (
    <footer className="d-flex flex-column align-items-center w-100 position-relative">
      <LogoImage imageUrl={logoUrl} />
      {languages.length >= 2 ? (
        <>
          <Typography
            variant="h4"
            className="text-center text-secondary font-weight-regular mb-4"
          >
            {t(`language:title`)}
          </Typography>
          <ButtonGroup>
            {languages.length > 2 ? (
              <MUIFooterLanguageButton
                variant="contained"
                onClick={() => setLanguageModal(true)}
              >
                <LocaleIcon
                  name={locale}
                  width="3.2rem"
                  className="position-absolute start-0 ms-5"
                />
                {t(`language:${locale as "en"}`)}
              </MUIFooterLanguageButton>
            ) : (
              languages.map(renderLanguage)
            )}
          </ButtonGroup>
          <AnimatePresence mode="wait">
            {languageModal && (
              <MUILanguageModal className="language-modal">
                <motion.div
                  {...anim(modalUp)}
                  className="language-modal__content"
                >
                  <Typography variant="h2" className="text-center pt-5">
                    {t(`language:title`)}
                  </Typography>
                  <div className="language-modal__list">
                    {languages.map(renderModalLanguage)}
                  </div>
                  <div className="language-modal__close" onClick={onClose}>
                    <CloseIcon width="6rem" />
                  </div>
                </motion.div>
              </MUILanguageModal>
            )}
          </AnimatePresence>
        </>
      ) : (
        <div style={{ height: "7rem" }} />
      )}
      {isAIConversationEnabled && !isConversationPage && (
        <ConversationButton
          variant="contained"
          onClick={() => navigate("/conversation")}
        >
          <Typography fontSize="3.6rem">
            <AISparkle />
          </Typography>
        </ConversationButton>
      )}
    </footer>
  );
});

export default Footer;
